// Copyright (c) Docugami, Inc. All rights reserved.

import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError, retry } from "@reduxjs/toolkit/dist/query"
import { Mutex } from "async-mutex"
import { authAccessor } from "authentication/authAccessor";

export interface DgQueryConfig {
  disableOrganizationIdHeader?: boolean;
  paramsSerializer?: (params: Record<string, any>) => string
}

export const createDgBaseQuery = (baseUrl?: string, config?: DgQueryConfig) => retry(withReauth(fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders: async (headers) => {
    const token = await authAccessor.getAccessToken();
    if (!!token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (!config?.disableOrganizationIdHeader) {
      const orgId = authAccessor.getOrganizationId();
      if (!!orgId) {
        headers.set('x-docugami-organization-id', orgId);
      }
    }

    return headers;
  },
  paramsSerializer: config?.paramsSerializer
})), { maxRetries: 0 });

const authMutex = new Mutex()
const withReauth = (baseQueryFn: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => async (args, api, extraOptions) => {
  await authMutex.waitForUnlock();

  let result = await baseQueryFn(args, api, extraOptions);
  if (!!result.error && result.error.status === 401) {
    if (!authMutex.isLocked()) {
      const release = await authMutex.acquire()
      try {
        result = await baseQueryFn(args, api, extraOptions)
      } finally {
        release()
      }
    }
    else {
      await authMutex.waitForUnlock()
      result = await baseQueryFn(args, api, extraOptions)
    }
  }

  return result;
}
